import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { TotemStatusBadge, getStatusLabel } from "./TotemStatusBadge";
import { useTotemMovements } from "../hooks/useTotemDetail";
import { Calendar, ArrowRight, User, FileText } from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

interface TotemMovementHistoryProps {
  totemId: string;
}

export function TotemMovementHistory({ totemId }: TotemMovementHistoryProps) {
  const { data: movements, isLoading } = useTotemMovements(totemId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Historial de Movimientos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!movements || movements.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Historial de Movimientos</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-4">
            No hay movimientos registrados
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Historial de Movimientos</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {movements.map((movement, index) => (
            <div
              key={movement.id}
              className={`flex gap-4 pb-4 ${
                index < movements.length - 1 ? "border-b" : ""
              }`}
            >
              {/* Timeline dot and line */}
              <div className="flex flex-col items-center">
                <div className="w-3 h-3 rounded-full bg-primary" />
                {index < movements.length - 1 && (
                  <div className="w-0.5 flex-1 bg-border mt-2" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 space-y-2">
                {/* Date */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {dayjs(movement.created_at).format("DD MMM YYYY, HH:mm")}
                </div>

                {/* Status transition */}
                <div className="flex items-center gap-2 flex-wrap">
                  {movement.from_status && (
                    <>
                      <TotemStatusBadge status={movement.from_status} size="sm" />
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </>
                  )}
                  <TotemStatusBadge status={movement.to_status} size="sm" />
                </div>

                {/* Details */}
                <div className="text-sm space-y-1">
                  {movement.performed_by_profile && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-3 w-3" />
                      {`${movement.performed_by_profile.name} ${movement.performed_by_profile.lastname}`}
                    </div>
                  )}

                  {movement.client && (
                    <div className="text-muted-foreground">
                      Cliente: {movement.client.name}
                    </div>
                  )}

                  {movement.notes && (
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <FileText className="h-3 w-3 mt-0.5" />
                      <span>{movement.notes}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
